import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  Chip,
  Divider,
  IconButton,
} from '@mui/material';
import {
  PlusIcon,
  PencilSimpleIcon,
  TrashSimpleIcon,
} from '@phosphor-icons/react';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import LocationAutocomplete from '../../../common/LocationAutoComplete';
import {
  useQuery,
  useMutation,
  useQueryClient,
  useQueries,
} from '@tanstack/react-query';
import {
  getAllBrandsApiV2,
  createStoreApiV2,
  updateStoreApi,
  deleteStore,
  getStoresApi,
  getGoogleLocationsGeocode,
  getStoreHours,
} from '../../../../utils/apis';
import { useUser } from '../../../../hooks/useUser';
import { useFetchBusiness } from '../../../../hooks/useFetchBusiness';
import { ICreateStoreV2 } from '../../../../schemas/apis/interface';
import { debounce } from 'lodash';
import MessagePopup from '../../../common/MessagePopup';
import StoreHoursDrawer from '../../FirstUserJourney/StoreHoursDrawer';
import { CircularProgress } from '@mui/material';

interface Store {
  id?: string;
  friendly_name: string;
  street_address: string;
  city: string;
  state_province: string;
  country: string;
  zip_code: string;
  mode_of_operation: ('delivery' | 'take_away')[];
  brand_ids: string[];
  poc_name: string;
  poc_email: string;
  poc_phone: string;
  poc_role: string;
  is_store_closed: boolean;
  ordering_enabled: boolean;
  hours?: WeeklySettings;
}

interface TimeSlot {
  from_time: string;
  to_time: string;
}

interface DaySettings {
  is_open: boolean;
  time_slots: TimeSlot[];
}

interface WeeklySettings {
  Monday: DaySettings;
  Tuesday: DaySettings;
  Wednesday: DaySettings;
  Thursday: DaySettings;
  Friday: DaySettings;
  Saturday: DaySettings;
  Sunday: DaySettings;
}

interface AddStoreStepProps {
  stores: Store[]; // Not used, kept for backwards compatibility
  onStoresChange: (stores: Store[]) => void; // Not used, kept for backwards compatibility
  onValidationChange: (isValid: boolean) => void;
}

const defaultDaySettings: DaySettings = {
  is_open: false,
  time_slots: [{ from_time: '09:00', to_time: '17:00' }],
};

const defaultWeeklySettings: WeeklySettings = {
  Monday: { ...defaultDaySettings },
  Tuesday: { ...defaultDaySettings },
  Wednesday: { ...defaultDaySettings },
  Thursday: { ...defaultDaySettings },
  Friday: { ...defaultDaySettings },
  Saturday: { ...defaultDaySettings },
  Sunday: { ...defaultDaySettings },
};

const AddStoreStep = ({ onValidationChange }: AddStoreStepProps) => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  const { business } = useFetchBusiness(user?.tenantName || '');

  // Fetch brands from API
  const { data: brandsData } = useQuery({
    queryKey: ['brands', user?.tenantName],
    queryFn: () => getAllBrandsApiV2(user?.tenantName || ''),
    enabled: !!user?.tenantName,
  });

  const brands = brandsData?.data?.data || [];

  // Fetch stores from API
  const { data: storesData, isLoading: storesLoading } = useQuery({
    queryKey: ['stores', user?.tenantName],
    queryFn: () => getStoresApi(user?.tenantName || ''),
    enabled: !!user?.tenantName,
  });

  // Access stores correctly - it's data.stores, not data.data. useMemo keeps a stable reference for useEffect deps.
  const stores = useMemo(
    () => storesData?.data?.stores || [],
    [storesData?.data?.stores]
  );

  console.log('Stores debug:', {
    storesData,
    stores,
    storesLength: stores.length,
    rawData: storesData?.data,
  });

  const [locationInput, setLocationInput] = useState('');
  const [selectionMade, setSelectionMade] = useState(false);
  const [showStoreForm, setShowStoreForm] = useState(false);
  const [editingStoreId, setEditingStoreId] = useState<number | null>(null);
  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [openHoursDrawer, setOpenHoursDrawer] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState<number | null>(null);

  const [formData, setFormData] = useState<Store>({
    friendly_name: '',
    street_address: '',
    city: '',
    state_province: '',
    country: '',
    zip_code: '',
    mode_of_operation: ['delivery', 'take_away'],
    brand_ids: [],
    poc_name: '',
    poc_email: '',
    poc_phone: '',
    poc_role: '',
    is_store_closed: false,
    ordering_enabled: true,
    hours: { ...defaultWeeklySettings },
  });

  // Mutations for store operations
  const createStore = useMutation({
    mutationFn: (formData: ICreateStoreV2) =>
      createStoreApiV2(formData, user?.tenantName || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', user?.tenantName] });
      setAlert({
        open: true,
        message: 'Outlet created successfully',
        severity: 'success',
      });
      setShowStoreForm(false);
      resetForm();
    },
    onError: () => {
      setAlert({
        open: true,
        message: 'Failed to create outlet',
        severity: 'error',
      });
    },
  });

  const updateStore = useMutation({
    mutationFn: ({
      formData,
      storeId,
    }: {
      formData: ICreateStoreV2;
      storeId: string;
    }) => updateStoreApi(formData, user?.tenantName || '', storeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', user?.tenantName] });
      setAlert({
        open: true,
        message: 'Outlet updated successfully',
        severity: 'success',
      });
      setShowStoreForm(false);
      setEditingStoreId(null);
      resetForm();
    },
    onError: () => {
      setAlert({
        open: true,
        message: 'Failed to update outlet',
        severity: 'error',
      });
    },
  });

  const deleteStoreMutation = useMutation({
    mutationFn: (storeId: string) =>
      deleteStore(storeId, user?.tenantName || ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stores', user?.tenantName] });
      setAlert({
        open: true,
        message: 'Outlet deleted successfully',
        severity: 'success',
      });
    },
    onError: () => {
      setAlert({
        open: true,
        message: 'Failed to delete outlet',
        severity: 'error',
      });
    },
  });

  // Helper function to reset form
  const resetForm = () => {
    setFormData({
      friendly_name: '',
      street_address: '',
      city: '',
      state_province: '',
      country: '',
      zip_code: '',
      mode_of_operation: ['delivery', 'take_away'],
      brand_ids: [],
      poc_name: '',
      poc_email: '',
      poc_phone: '',
      poc_role: '',
      is_store_closed: false,
      ordering_enabled: true,
      hours: { ...defaultWeeklySettings },
    });
    setLocationInput('');
    setSelectionMade(false);
  };

  // Fetch store hours for all stores
  const fetchStoreHours = async (storeId: number, tenantName: string) => {
    const response = await getStoreHours(storeId.toString(), tenantName);
    return response.data;
  };

  const storeHoursQueries = useQueries({
    queries: (stores || []).map((store: any) => ({
      queryKey: ['storeHours', store.id, user?.tenantName],
      queryFn: () => fetchStoreHours(store.id, user?.tenantName || ''),
      enabled: !!user?.tenantName && !!store.id,
      staleTime: Infinity,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
    })),
  });

  // Handler to open hours drawer
  const handleOpenHours = (storeId: number) => {
    setSelectedStoreId(storeId);
    setOpenHoursDrawer(true);
  };

  const showStoreAlert = (message: string, severity: 'success' | 'error') => {
    setAlert({
      open: true,
      message,
      severity,
    });
  };

  // Debounced geocoding function
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedGeocode = useCallback(
    debounce(async (location: string) => {
      if (!location.trim()) return;

      try {
        const geocodeResponse = await getGoogleLocationsGeocode({
          query: location,
        });

        const geocodeData = geocodeResponse.data.results[0];
        const addressComponents = geocodeData.address_components;

        const getComponent = (type: string, short = false) => {
          const component = addressComponents.find((comp: any) =>
            comp.types.includes(type)
          );
          return component
            ? short
              ? component.short_name
              : component.long_name
            : '';
        };

        setFormData(prev => ({
          ...prev,
          street_address: geocodeData.formatted_address || locationInput,
          city:
            getComponent('locality') ||
            getComponent('administrative_area_level_2'),
          state_province: getComponent('administrative_area_level_1'),
          country: getComponent('country'),
          zip_code: getComponent('postal_code'),
        }));
      } catch (error) {
        console.error('Error fetching geocode data:', error);
      }
    }, 500),
    []
  );

  useEffect(() => {
    // Valid if at least one store exists
    const isValid = stores.length > 0;
    console.log('[AddStoreStep] Validation check:', {
      storesCount: stores.length,
      isValid,
      stores,
    });
    onValidationChange(isValid);
  }, [stores, onValidationChange]);

  // Only run once when stores are first loaded
  useEffect(() => {
    // Show form by default if no stores exist (only on initial load)
    if (stores.length === 0 && !storesLoading) {
      setShowStoreForm(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storesLoading]); // Only depends on loading state changing

  const handleLocationChange = (location: string) => {
    setLocationInput(location);

    // If this is a selection from dropdown (longer text that suddenly appears)
    if (location.length > locationInput.length + 10 || selectionMade) {
      setSelectionMade(true);

      // Call geocode API for the selected location
      debouncedGeocode(location);
    }

    // If the input is cleared or significantly shortened, reset the selection flag
    if (location.length < 3) {
      setSelectionMade(false);
    }
  };

  const handleSubmitLocation = () => {
    // If user has typed something but not selected from dropdown
    if (locationInput && !selectionMade) {
      // Call geocode API for the custom location
      debouncedGeocode(locationInput);
    }
  };

  const handleBrandSelect = (brandId: string) => {
    if (!formData.brand_ids.includes(brandId)) {
      setFormData(prev => ({
        ...prev,
        brand_ids: [...prev.brand_ids, brandId],
      }));
    }
  };

  const handleBrandRemove = (brandId: string) => {
    setFormData(prev => ({
      ...prev,
      brand_ids: prev.brand_ids.filter(id => id !== brandId),
    }));
  };

  const handleStoreSave = async () => {
    if (
      !formData.friendly_name ||
      !formData.poc_name ||
      !formData.poc_email ||
      !formData.poc_phone ||
      !formData.street_address ||
      formData.brand_ids.length === 0
    ) {
      setAlert({
        open: true,
        message: 'Please fill in all required fields',
        severity: 'error',
      });
      return;
    }

    // Process any pending location changes
    handleSubmitLocation();

    // Wait for any pending geocode operations
    await new Promise(resolve => setTimeout(resolve, 600));

    const sanitizedData: ICreateStoreV2 = {
      friendly_name: formData.friendly_name,
      poc_name: formData.poc_name,
      poc_email: formData.poc_email,
      poc_phone: formData.poc_phone,
      poc_role: formData.poc_role,
      mode_of_operation: ['delivery', 'take_away'],
      brand_ids: formData.brand_ids,
      street_address: formData.street_address || '',
      zip_code: formData.zip_code || '',
      city: formData.city || '',
      country: formData.country || '',
      state_province: formData.state_province || '',
      business_id: business?.business_id || 0,
      is_store_closed: false,
      ordering_enabled: true,
    };

    if (editingStoreId) {
      await updateStore.mutateAsync({
        formData: sanitizedData,
        storeId: editingStoreId.toString(),
      });
    } else {
      await createStore.mutateAsync(sanitizedData);
    }
  };

  const handleEditStore = (storeId: number) => {
    const storeToEdit = stores.find((s: any) => s.id === storeId);
    if (storeToEdit) {
      setFormData({
        friendly_name: storeToEdit.friendly_name || '',
        street_address: storeToEdit.street_address || '',
        city: storeToEdit.city || '',
        state_province: storeToEdit.state_province || '',
        country: storeToEdit.country || '',
        zip_code: storeToEdit.zip_code || '',
        mode_of_operation: storeToEdit.mode_of_operation || [],
        brand_ids: storeToEdit.brand_ids || [],
        poc_name: storeToEdit.poc_name || '',
        poc_email: storeToEdit.poc_email || '',
        poc_phone: storeToEdit.poc_phone || '',
        poc_role: storeToEdit.poc_role || '',
        is_store_closed: storeToEdit.is_store_closed || false,
        ordering_enabled: storeToEdit.ordering_enabled !== false,
        hours: storeToEdit.hours || { ...defaultWeeklySettings },
      });
      setLocationInput(storeToEdit.street_address || '');
      setSelectionMade(true);
      setEditingStoreId(storeId);
      setShowStoreForm(true);
    }
  };

  const handleDeleteStore = async (storeId: number) => {
    if (confirm('Are you sure you want to delete this outlet?')) {
      await deleteStoreMutation.mutateAsync(storeId.toString());
    }
  };

  const handleCancelEdit = () => {
    setShowStoreForm(false);
    setEditingStoreId(null);
    resetForm();
  };

  const isPending = createStore.isPending || updateStore.isPending;

  return (
    <Box
      sx={{
        maxWidth: '1200px',
        width: '100%',
        px: 2,
        py: 3,
        mx: 'auto',
      }}
    >
      {/* Header - aligned with form */}
      <Box sx={{ maxWidth: '900px', mx: 'auto', mb: 3 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'custom.textBlue',
            mb: 1,
            fontSize: '18px',
          }}
        >
          Add Outlet
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'lightGray.text',
            fontSize: '12px',
          }}
        >
          Add and manage your outlet locations for accurate customer
          interactions
        </Typography>
      </Box>

      {/* Store List - Show when stores exist and form is not shown */}
      {stores.length > 0 && !showStoreForm && (
        <Box sx={{ maxWidth: '900px', mx: 'auto', mb: 3 }}>
          {stores.map((store: any, index: number) => {
            // Get the corresponding query result for store hours
            const storeHoursQuery = storeHoursQueries[index];
            const storeHoursData = storeHoursQuery?.data;
            const storeHoursLoading = storeHoursQuery?.isLoading;

            // Check if store has hours
            const hasHours =
              storeHoursData &&
              // @ts-expect-error - API response type
              Array.isArray(storeHoursData.opening_hours) &&
              // @ts-expect-error - API response type
              storeHoursData.opening_hours.some(
                (day: any) =>
                  day.is_open &&
                  Array.isArray(day.time_slots) &&
                  day.time_slots.length > 0
              );

            return (
              <Box
                key={store.id}
                sx={{
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  mb: 2,
                  overflow: 'hidden',
                }}
              >
                {/* Store Info Section */}
                <Box
                  sx={{
                    p: 2,
                    bgcolor: '#F5F5FF',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '16px',
                        color: 'custom.textBlue',
                        mb: 0.5,
                      }}
                    >
                      {store.friendly_name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: 'lightGray.text',
                        mb: 0.5,
                      }}
                    >
                      <span style={{ fontWeight: 500 }}>Location:</span>{' '}
                      {store.street_address}, {store.city},{' '}
                      {store.state_province}, {store.country}
                    </Typography>
                    {store.brand_ids && store.brand_ids.length > 0 && (
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: 'lightGray.text',
                        }}
                      >
                        <span style={{ fontWeight: 500 }}>Brands:</span>{' '}
                        {store.brand_ids
                          .map(
                            (id: string) =>
                              brands.find((b: any) => b.id === id)?.name
                          )
                          .filter(Boolean)
                          .join(', ')}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditStore(store.id)}
                      sx={{
                        backgroundColor: '#FEFEFE',
                        '&:hover': {
                          backgroundColor: '#FEFEFE',
                          scale: 1.1,
                        },
                        width: '30px',
                        height: '30px',
                      }}
                    >
                      <PencilSimpleIcon
                        size={14}
                        color="#6B6F80"
                        weight="duotone"
                      />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteStore(store.id)}
                      sx={{
                        backgroundColor: 'rgba(231, 76, 60, 0.15)',
                        '&:hover': {
                          backgroundColor: 'rgba(231, 76, 60, 0.15)',
                          scale: 1.1,
                        },
                        width: '30px',
                        height: '30px',
                      }}
                    >
                      <TrashSimpleIcon
                        size={14}
                        color="#E74C3C"
                        weight="fill"
                      />
                    </IconButton>
                  </Box>
                </Box>

                {/* Store Hours Section */}
                <Box
                  sx={{
                    padding: '16px 24px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    bgcolor: 'background.paper',
                    borderTop: '1px solid #E2E8F0',
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 400,
                      color: 'custom.textBlue',
                      fontSize: '14px',
                    }}
                  >
                    Outlet Operation Hours
                  </Typography>
                  <Button
                    onClick={() => handleOpenHours(store.id)}
                    sx={{
                      color: '#6366F1',
                      fontSize: 14,
                      textTransform: 'none',
                    }}
                    variant={hasHours ? 'text' : 'outlined'}
                    disabled={storeHoursLoading}
                    startIcon={
                      storeHoursLoading ? (
                        <CircularProgress size={16} />
                      ) : !hasHours ? (
                        <PlusIcon size={16} />
                      ) : null
                    }
                  >
                    {storeHoursLoading
                      ? 'Loading...'
                      : hasHours
                        ? 'Edit Opening Hours'
                        : 'Add Opening Hours'}
                  </Button>
                </Box>
              </Box>
            );
          })}

          {/* Add New Store Button */}
          <Button
            variant="outlined"
            startIcon={<PlusIcon size={16} />}
            onClick={() => setShowStoreForm(true)}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
              height: '40px',
              fontSize: '14px',
              fontWeight: 500,
              width: '100%',
            }}
          >
            Add New Outlet
          </Button>
        </Box>
      )}

      {/* Store Form - Show when no stores or showStoreForm is true */}
      {(stores.length === 0 || showStoreForm) && (
        <Box
          sx={{
            maxWidth: '900px',
            mx: 'auto',
            p: 4,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'lightGray.main',
            borderRadius: '8px',
            mb: 3,
          }}
        >
          {/* Store Details Section */}
          <Typography
            sx={{
              fontSize: 16,
              color: 'custom.textBlue',
              mb: 2.5,
              lineHeight: 1.5,
              fontWeight: 500,
            }}
          >
            Outlet Details
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
            {/* Store Address */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  minWidth: '100px',
                  fontWeight: 500,
                  fontSize: 14,
                  color: 'lightGray.text',
                }}
              >
                Outlet Address
              </Typography>
              <Box sx={{ width: '100%' }}>
                <LocationAutocomplete
                  value={locationInput}
                  onChange={handleLocationChange}
                  placeholder="Enter outlet address"
                  inputStyle={{}}
                />
              </Box>
            </Box>

            {/* Store Alias */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                sx={{
                  minWidth: '100px',
                  fontWeight: 500,
                  fontSize: 14,
                  color: 'lightGray.text',
                }}
              >
                Outlet Alias
              </Typography>
              <TextField
                placeholder="Enter outlet alias"
                value={formData.friendly_name}
                onChange={e =>
                  setFormData(prev => ({
                    ...prev,
                    friendly_name: e.target.value,
                  }))
                }
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    height: '40px',
                    borderRadius: '6px',
                    backgroundColor: 'background.paper',
                    '& fieldset': {
                      borderWidth: '1px',
                      borderColor: 'lightGray.main',
                    },
                    '&:hover fieldset': {
                      borderColor: '#D0D5DD',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'primary.main',
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '8px 12px',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* Available Brands */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                sx={{
                  minWidth: '110px',
                  fontWeight: 500,
                  fontSize: 14,
                  color: 'lightGray.text',
                }}
              >
                Available Brands
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1,
                  flex: 1,
                  minHeight: '40px',
                  bgcolor: 'background.paper',
                  p: 1,
                  borderRadius: '8px',
                  border: '1px solid',
                  borderColor: 'lightGray.main',
                }}
              >
                {formData.brand_ids.map(brandId => {
                  const brand = brands.find((b: any) => b.id === brandId);
                  return (
                    brand && (
                      <Chip
                        key={brand.id}
                        label={brand.name}
                        onDelete={() => handleBrandRemove(brand.id)}
                        sx={{
                          height: '24px',
                          fontSize: '12px',
                          bgcolor: 'background.paper',
                          border: '1px solid',
                          borderColor: 'lightGray.main',
                        }}
                      />
                    )
                  );
                })}
              </Box>
              <Select
                variant="outlined"
                size="small"
                onChange={e => handleBrandSelect(e.target.value)}
                value=""
                displayEmpty
                renderValue={() => 'Select brand'}
                sx={{
                  minWidth: '116px',
                  height: '40px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  bgcolor: 'secondary.main',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'lightGray.main',
                  },
                }}
              >
                {brands.filter(
                  (brand: any) => !formData.brand_ids.includes(brand.id)
                ).length === 0 ? (
                  <MenuItem disabled>No brands available</MenuItem>
                ) : (
                  brands
                    .filter(
                      (brand: any) => !formData.brand_ids.includes(brand.id)
                    )
                    .map((brand: any) => (
                      <MenuItem key={brand.id} value={brand.id}>
                        {brand.name}
                      </MenuItem>
                    ))
                )}
              </Select>
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)', my: 3 }} />

          {/* Store Contact Details Section */}
          <Typography
            sx={{
              fontSize: 16,
              color: 'custom.textBlue',
              mb: 2.5,
              lineHeight: 1.5,
              fontWeight: 500,
            }}
          >
            Outlet Contact Details
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Row 1: Name and Email */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
              >
                <Typography
                  sx={{
                    minWidth: '45px',
                    fontWeight: 500,
                    fontSize: 14,
                    color: 'lightGray.text',
                  }}
                >
                  Name
                </Typography>
                <TextField
                  placeholder="Enter name"
                  value={formData.poc_name}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, poc_name: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: 'background.paper',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: 'lightGray.main',
                      },
                      '&:hover fieldset': {
                        borderColor: '#D0D5DD',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                    },
                  }}
                />
              </Box>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
              >
                <Typography
                  sx={{
                    minWidth: '45px',
                    fontWeight: 500,
                    fontSize: 14,
                    color: 'lightGray.text',
                  }}
                >
                  Email
                </Typography>
                <TextField
                  placeholder="example@gmail.com"
                  value={formData.poc_email}
                  onChange={e =>
                    setFormData(prev => ({
                      ...prev,
                      poc_email: e.target.value,
                    }))
                  }
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: 'background.paper',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: 'lightGray.main',
                      },
                      '&:hover fieldset': {
                        borderColor: '#D0D5DD',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Row 2: Role and Contact */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
              >
                <Typography
                  sx={{
                    minWidth: '45px',
                    fontWeight: 500,
                    fontSize: 14,
                    color: 'lightGray.text',
                  }}
                >
                  Role
                </Typography>
                <TextField
                  placeholder="Enter role"
                  value={formData.poc_role}
                  onChange={e =>
                    setFormData(prev => ({ ...prev, poc_role: e.target.value }))
                  }
                  fullWidth
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      height: '40px',
                      borderRadius: '6px',
                      backgroundColor: 'background.paper',
                      '& fieldset': {
                        borderWidth: '1px',
                        borderColor: 'lightGray.main',
                      },
                      '&:hover fieldset': {
                        borderColor: '#D0D5DD',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: '1px',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      padding: '8px 12px',
                      fontSize: '14px',
                    },
                  }}
                />
              </Box>

              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}
              >
                <Typography
                  sx={{
                    minWidth: '45px',
                    fontWeight: 500,
                    fontSize: 14,
                    color: 'lightGray.text',
                  }}
                >
                  Contact
                </Typography>
                <Box sx={{ width: '100%' }}>
                  <PhoneInput
                    country="us"
                    value={formData.poc_phone}
                    onChange={phone =>
                      setFormData(prev => ({ ...prev, poc_phone: phone }))
                    }
                    placeholder=""
                    enableSearch={false}
                    disableSearchIcon={true}
                    disableDropdown={false}
                    searchPlaceholder=""
                    inputProps={{ placeholder: '' }}
                    specialLabel=""
                    buttonStyle={{
                      borderRadius: '8px 0 0 8px',
                      border: '1px solid #E2E8F0',
                      backgroundColor: 'white',
                    }}
                    inputStyle={{
                      width: '100%',
                      height: '40px',
                      fontSize: '14px',
                      backgroundColor: '#FFFFFF',
                      borderRadius: '0 8px 8px 0',
                      border: '1px solid #E2E8F0',
                    }}
                    containerStyle={{
                      width: '100%',
                    }}
                    dropdownStyle={{
                      boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
                      borderRadius: '6px',
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)', my: 3 }} />

          {/* Action Buttons */}
          <Box
            sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}
          >
            {stores.length > 0 && (
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                disabled={isPending}
                sx={{
                  borderColor: 'lightGray.main',
                  color: 'custom.textBlue',
                  textTransform: 'none',
                  borderRadius: '8px',
                  height: '34px',
                  px: 3,
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>
            )}
            <Button
              variant="contained"
              onClick={handleStoreSave}
              disabled={isPending}
              startIcon={
                isPending ? null : editingStoreId ? (
                  <PencilSimpleIcon size={16} />
                ) : (
                  <PlusIcon size={16} />
                )
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                textTransform: 'none',
                borderRadius: '8px',
                height: '34px',
                px: 3,
                fontSize: '14px',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': {
                  backgroundColor: 'custom.tertiary',
                  boxShadow: 'none',
                },
                '&:disabled': {
                  backgroundColor: 'lightGray.main',
                  color: 'lightGray.text',
                },
              }}
            >
              {isPending
                ? editingStoreId
                  ? 'Updating...'
                  : 'Adding...'
                : editingStoreId
                  ? 'Update Outlet'
                  : 'Add Outlet'}
            </Button>
          </Box>
        </Box>
      )}

      {/* MessagePopup for alerts */}
      <MessagePopup alert={alert} setAlert={setAlert} />

      {/* Store Hours Drawer */}
      <StoreHoursDrawer
        open={openHoursDrawer}
        onClose={() => setOpenHoursDrawer(false)}
        selectedStoreId={selectedStoreId}
        tenantName={user?.tenantName || ''}
        showAlert={showStoreAlert}
        existingHours={
          storeHoursQueries.find(
            // @ts-expect-error - API response type
            query => query.data && query.data.store?.id === selectedStoreId
          )?.data
        }
      />
    </Box>
  );
};

export default AddStoreStep;
